const PORTAL_ROOT = ".folloportal.com";

const DEV_ENV_LABEL = "dev";

const LOCAL_HOSTNAMES = new Set(["localhost", "127.0.0.1"]);

/**
 * The environment label in a portal hostname.
 *
 * The portal is one build for every environment, so the hostname is the only
 * thing that tells them apart: production is <tenant>.folloportal.com, every
 * other environment <tenant>.<env>.folloportal.com — the same shape
 * `getSubdomainFromCurrentUrl` in @follosoft/react-components reads the tenant
 * from.
 *
 * Returns the label ("dev", "staging", ...), "" for a production tenant host,
 * and null for a host of any other shape.
 */
export const getPortalEnvLabel = (hostname: string): string | null => {
  const host = hostname.toLowerCase();

  if (!host.endsWith(PORTAL_ROOT)) {
    return null;
  }

  const labels = host.slice(0, -PORTAL_ROOT.length).split(".");

  if (labels.some((label) => label === "")) {
    return null;
  }

  if (labels.length === 1) {
    return "";
  }

  return labels.length === 2 ? labels[1] : null;
};

/** <tenant>.dev.folloportal.com — the only environment whose API host can be swapped. */
export const isDevPortalHost = (hostname: string): boolean =>
  getPortalEnvLabel(hostname) === DEV_ENV_LABEL;

/**
 * Whether the debug panel may open on this host: any environment that is not
 * production. A host whose environment cannot be read off its name counts as
 * production, so the panel never opens anywhere it was not meant to.
 */
export const isDebugPanelHost = (hostname: string): boolean =>
  LOCAL_HOSTNAMES.has(hostname) || Boolean(getPortalEnvLabel(hostname));
